import React, { FC, useCallback, useEffect, useState } from 'react';
import { Box, Slider } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Cropper from 'cropperjs';
import i18n from '../../../i18n';

import { Icon } from 'components/Icon/Icon';
import { Text, TextTypography } from 'components/Text/Text';
import { Image, ImageSize } from 'components/Image/Image';

import { CroppedArea } from 'types';

import { ReactComponent as TrashIcon } from 'assets/icons/main-16/trash-o--16.svg';

import 'cropperjs/dist/cropper.css';

import { CropperMode } from './types';
import { initCropper, initProductImageCropper } from './cropper';
import css from './CoverImageControlContents.module.css';

export interface CoverImageControlContentsProps {
    mode: CropperMode;
    value: string;
    onDelete: () => void;
    cropSize: {
        width: number;
        height: number;
    };
    cropAspect: number;
    onCropComplete: (value: CroppedArea) => void;
    previewElement: Element;
    hideRemoveButton?: boolean;
}

const useStyles = makeStyles(theme => ({
    cropContainer: {
        position: 'relative',
        width: '100%',
        height: 296,
        background: '#333',
        [theme.breakpoints.up('sm')]: {
            height: 296,
        },
        '& .cropper-modal': {
            background: '#0F1825',
            opacity: 0.6,
        },
    },
    sliderContainer: {
        display: 'flex',
        flex: '1',
        alignItems: 'center',
        flexDirection: 'column',
        maxWidth: 400,
    },
    sliderWrapper: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    removeButton: {
        backgroundColor: theme.palette.fixedWhite.secondary,
        borderRadius: theme.spacing(0.75),
        bottom: theme.spacing(1),
        height: theme.spacing(4),
        paddingLeft: theme.spacing(1),
        paddingTop: theme.spacing(1),
        position: 'absolute',
        right: theme.spacing(1),
        width: theme.spacing(4),
        zIndex: 1,
    },
    helpText: {
        position: 'absolute',
        bottom: 0,
        width: '100%',
        textAlign: 'center',
        padding: '0px 10px',
    },
}));

export const CoverImageControlContents: FC<CoverImageControlContentsProps> = ({
    mode,
    value,
    cropSize,
    cropAspect,
    onDelete,
    onCropComplete,
    previewElement,
    hideRemoveButton,
}) => {
    const classes = useStyles({ cropHeight: cropSize.height });
    const { t } = i18n.useTranslation();

    const [isReady, setIsReady] = useState(false);

    const [zoom, setZoom] = useState(1);
    const [minZoom, setMinZoom] = useState(0);

    const [element, setElement] = useState<HTMLImageElement | null>(null);
    const [cropper, setCropper] = useState<Cropper | null>(null);
    useEffect(() => {
        if (!element || !value) {
            return;
        }
        setIsReady(false);
        if (cropper) {
            cropper.destroy();
        }
        const cropperInstance = new Cropper(element, {
            aspectRatio: cropAspect,
            cropBoxMovable: false,
            cropBoxResizable: false,
            viewMode: mode === CropperMode.PRODUCT_IMAGE ? 0 : 1,
            preview: [previewElement],
            ready() {
                const { cropper } = this as { cropper: Cropper };

                if (mode === CropperMode.PRODUCT_IMAGE) {
                    const initialZoom = initProductImageCropper(cropper);
                    setZoom(initialZoom);
                    setMinZoom(initialZoom);
                } else {
                    const initialZoom = initCropper(cropper);
                    setZoom(initialZoom);
                    setMinZoom(initialZoom / 2);
                }

                setIsReady(true);
            },
            crop(event: Cropper.CropEvent) {
                const { x, y, width, height } = event.detail;
                onCropComplete({ width, height, x, y });
            },
            zoom(event: Cropper.ZoomEvent) {
                setZoom(event.detail.ratio);
            },
        });
        setCropper(cropperInstance);
    }, [cropAspect, element, onCropComplete, setIsReady, previewElement, setZoom, value, setMinZoom, setCropper]);

    const handleZoomChange = useCallback(
        (_, value: number | number[]) => {
            const zoom = Array.isArray(value) ? value[0] : value;
            setZoom(zoom);
            if (cropper) {
                cropper.zoomTo(zoom);
            }
        },
        [cropper, setZoom],
    );

    const isEditable = value.startsWith('blob');

    return (
        <div>
            <div className={classes.cropContainer}>
                {isEditable ? (
                    <img
                        style={{
                            display: 'block',
                            maxWidth: '100%',
                            visibility: isReady ? 'visible' : 'hidden',
                        }}
                        ref={setElement}
                        src={value}
                        alt=""
                    />
                ) : (
                    <Image imageId={value} description="" size={ImageSize.RECTANGULAR} />
                )}
                {!hideRemoveButton && (
                    <div className={classes.removeButton} onClick={onDelete}>
                        <Icon component={TrashIcon} size={16} svgSize={16} />
                    </div>
                )}
                {isEditable && (
                    <div className={classes.helpText}>
                        <Text typography={TextTypography.PARA} className={css.text_color_white}>
                            {t('common:imageControl.moveToChangePosition')}
                        </Text>
                    </div>
                )}
            </div>
            {cropper && (
                <Box m={2} className={classes.sliderWrapper}>
                    <div className={classes.sliderContainer}>
                        <Text typography={TextTypography.PARA}>{t('common:imageControl.zoom')}</Text>
                        <Slider
                            disabled={minZoom === 0}
                            value={zoom}
                            min={minZoom}
                            max={1}
                            step={0.1}
                            onChange={handleZoomChange}
                        />
                    </div>
                </Box>
            )}
        </div>
    );
};
